import { useState } from "react";
import { NavLink } from "react-router-dom";
import * as m from "motion/react-m";
import {
  ArrowDown,
  ArrowRight,
  BriefcaseBusiness,
  FileText,
  Globe2,
  Linkedin,
  MessageCircleMore,
  ScanSearch,
  Sparkles,
} from "lucide-react";
import { pricingTiers } from "../constants/content";
import {
  HeroArtifactPositioner,
  EvidenceCaption,
  EvidenceStoryMotion,
  MethodProgress,
  Reveal,
  RuleReveal,
  Stagger,
  motionTokens,
  useLandingMobile,
  useMediaQuery,
} from "../motion";
import { LandingMotionProvider } from "../motion/LandingMotionProvider";
import { featuredPortfolio } from "../constants/featuredPortfolio";
import "./CinematicLanding.css";

const heroHeadlineLines = ["Your story.", "Unmistakable in", "every format."];

const identityPillars = [
  ["Role", featuredPortfolio.identity.role, "Research-driven engineering grounded in mechanics, mathematics, and physical evidence."],
  ["Focus", featuredPortfolio.identity.focus, "Energy conversion, computational mechanics, and rigorous experimental work."],
  ["Edge", featuredPortfolio.identity.edge, "Moves from governing equations to simulations, prototypes, and measured results."],
];

const outputs = [
  {
    id: "resume",
    label: "ATS Résumé",
    ...featuredPortfolio.resume,
    icon: FileText,
    document: true,
  },
  {
    id: "identity",
    label: "Identity Spec",
    ...featuredPortfolio.identitySpec,
    signal: ["ROLE", "FOCUS", "EDGE"],
    icon: ScanSearch,
    href: "/assets/landing/aarush-identity-spec.html",
  },
  {
    id: "linkedin",
    label: "LinkedIn Profile",
    ...featuredPortfolio.linkedin,
    title: featuredPortfolio.name,
    body: featuredPortfolio.linkedin.headline,
    signal: ["DERIVE", "MODEL", "VALIDATE"],
    icon: Linkedin,
    portrait: true,
  },
  {
    id: "sop",
    label: "Statement of Purpose",
    ...featuredPortfolio.sop,
    signal: ["PAST", "EVIDENCE", "DIRECTION"],
    icon: BriefcaseBusiness,
  },
];

const heroArtifacts = [
  { id: "resume", label: "ATS Résumé", icon: FileText, desktopOffset: 42, mobileOffset: 34, zIndex: 4, entranceOrder: 3 },
  { id: "identity", label: "Identity Spec", icon: ScanSearch, desktopOffset: 88, mobileOffset: 68, zIndex: 3, entranceOrder: 2 },
  { id: "linkedin", label: "LinkedIn Profile", icon: Linkedin, desktopOffset: 138, mobileOffset: 103, zIndex: 2, entranceOrder: 1 },
  { id: "sop", label: "Statement of Purpose", icon: BriefcaseBusiness, desktopOffset: 188, mobileOffset: 138, zIndex: 1, entranceOrder: 0 },
];

function Brand() {
  return (
    <NavLink className="cinematic-brand" to="/" aria-label="Prime Portfolio home">
      <span aria-hidden="true">P</span>
      <strong>Prime Portfolio</strong>
    </NavLink>
  );
}

function PortfolioSurface({ active, onSelect }) {
  return (
    <article
      className={`portfolio-surface ${active ? "is-active" : ""}`}
      aria-label={`${featuredPortfolio.name} portfolio preview`}
    >
      <button
        type="button"
        className="surface-interaction"
        aria-label="Bring the published portfolio to the front"
        aria-pressed={active}
        data-hero-artifact-button="portfolio"
        onClick={onSelect}
      />
      <img
        src={featuredPortfolio.assets.portfolio}
        alt={`${featuredPortfolio.name}’s published Prime Portfolio homepage`}
        loading="eager"
        decoding="async"
        fetchPriority="high"
      />
    </article>
  );
}

function HeroArtifactPreview({ id }) {
  if (id === "resume") {
    return (
      <div className="hero-artifact-preview hero-resume-preview" aria-hidden="true">
        <img src={featuredPortfolio.assets.resumePreview} alt="" loading="lazy" decoding="async" />
      </div>
    );
  }

  if (id === "identity") {
    return (
      <div className="hero-artifact-preview hero-identity-preview" aria-hidden="true">
        <p>Identity distilled from evidence</p>
        <dl>
          <div><dt>Role</dt><dd>{featuredPortfolio.identity.role}</dd></div>
          <div><dt>Focus</dt><dd>Energy systems and applied mathematics</dd></div>
          <div><dt>Edge</dt><dd>Derive, build, and validate</dd></div>
        </dl>
      </div>
    );
  }

  if (id === "linkedin") {
    return (
      <div className="hero-artifact-preview hero-linkedin-preview" aria-hidden="true">
        <img src={featuredPortfolio.assets.portrait} alt="" loading="lazy" decoding="async" />
        <div>
          <strong>{featuredPortfolio.name}</strong>
          <span>Mechanical Engineering | Applied Mathematics</span>
          <p>{featuredPortfolio.identity.promise}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-artifact-preview hero-sop-preview" aria-hidden="true">
      <p>Statement of Purpose</p>
      <h3>{featuredPortfolio.sop.title}</h3>
      <span>I want to deepen the connection between analytical modeling, simulation, and experimental energy research.</span>
    </div>
  );
}

function HeroArtifactButton({ active, artifact, onSelect }) {
  const Icon = artifact.icon;
  return (
    <article
      className={`hero-artifact-card hero-artifact-${artifact.id} ${active ? "is-active" : ""}`}
    >
      <HeroArtifactPreview id={artifact.id} />
      <button
        type="button"
        className="hero-artifact-tab"
        aria-label={active ? `Return the published portfolio to the front from ${artifact.label}` : `Bring ${artifact.label} to the front`}
        aria-pressed={active}
        data-hero-artifact-button={artifact.id}
        onClick={onSelect}
      >
        <span><Icon size={15} aria-hidden="true" />{artifact.label}</span>
        <small>{active ? "Selected · return" : "Select"}</small>
      </button>
    </article>
  );
}

function HeroHeadline({ isMobile }) {
  if (isMobile) {
    return (
      <h1 data-testid="landing-hero-heading" data-motion-sequence="hero-headline" data-motion-mode="independent">
        {heroHeadlineLines.map((line) => (
          <Reveal
            as="span"
            className="cinematic-headline-line"
            distance={motionTokens.distance.standard}
            duration={motionTokens.duration.supporting}
            key={line}
          >
            {line}
          </Reveal>
        ))}
      </h1>
    );
  }

  return (
    <Stagger
      as="h1"
      data-testid="landing-hero-heading"
      data-motion-sequence="hero-headline"
      data-motion-mode="grouped"
      delay={motionTokens.stagger.heroLine}
      stagger={motionTokens.stagger.heroLine}
    >
      {heroHeadlineLines.map((line) => (
        <Stagger.Item
          as="span"
          className="cinematic-headline-line"
          distance={motionTokens.distance.hero}
          duration={motionTokens.duration.hero}
          key={line}
        >
          {line}
        </Stagger.Item>
      ))}
    </Stagger>
  );
}

function HeroPackage({ isMobile }) {
  const [activeArtifact, setActiveArtifact] = useState("portfolio");
  const layers = [
    { id: "portfolio", desktopOffset: -34, mobileOffset: -22, zIndex: 5, entranceOrder: 4 },
    ...heroArtifacts,
  ];

  const selectArtifact = (id) => {
    setActiveArtifact((current) => (current === id && id !== "portfolio" ? "portfolio" : id));
  };

  const renderLayer = (artifact, animated) => {
    const active = activeArtifact === artifact.id;
    const content = (
      <HeroArtifactPositioner
        active={active}
        className="hero-artifact-positioner"
        data-artifact-active={active ? "true" : "false"}
        offset={isMobile ? artifact.mobileOffset : artifact.desktopOffset}
        zIndex={artifact.zIndex}
      >
        {artifact.id === "portfolio" ? (
          <PortfolioSurface active={active} onSelect={() => selectArtifact("portfolio")} />
        ) : (
          <HeroArtifactButton active={active} artifact={artifact} onSelect={() => selectArtifact(artifact.id)} />
        )}
      </HeroArtifactPositioner>
    );

    if (!animated) {
      return (
        <div
          className="hero-package-motion-layer"
          data-package-layer={artifact.id}
          key={artifact.id}
          style={{ zIndex: active ? 20 : artifact.zIndex }}
        >
          {content}
        </div>
      );
    }

    return (
      <Stagger.Item
        as="div"
        className="hero-package-motion-layer"
        data-package-layer={artifact.id}
        delay={(artifact.entranceOrder + 1) * motionTokens.stagger.standard}
        distance={motionTokens.distance.package}
        duration={motionTokens.duration.deliberate}
        key={artifact.id}
        style={{ zIndex: active ? 20 : artifact.zIndex }}
      >
        {content}
      </Stagger.Item>
    );
  };

  if (isMobile) {
    return (
      <div className="hero-package" aria-label="A portfolio and career documents created from one interview" data-active-artifact={activeArtifact}>
        <Reveal
          as="div"
          className="hero-package-sequence"
          data-motion-sequence="hero-package"
          data-motion-mode="independent"
          distance={motionTokens.distance.standard}
          duration={motionTokens.duration.quick}
        >
          {layers.map((artifact) => renderLayer(artifact, false))}
        </Reveal>
        <p className="hero-package-status" aria-live="polite">{activeArtifact === "portfolio" ? "Published portfolio selected" : `${heroArtifacts.find((artifact) => artifact.id === activeArtifact)?.label} selected`}</p>
      </div>
    );
  }

  return (
    <div className="hero-package" aria-label="A portfolio and career documents created from one interview" data-active-artifact={activeArtifact}>
      <Stagger
        as="div"
        className="hero-package-sequence"
        data-motion-sequence="hero-package"
        data-motion-mode="grouped"
        delay={0}
        stagger={0}
      >
        {layers.map((artifact) => renderLayer(artifact, true))}
      </Stagger>
      <p className="hero-package-status" aria-live="polite">{activeArtifact === "portfolio" ? "Published portfolio selected" : `${heroArtifacts.find((artifact) => artifact.id === activeArtifact)?.label} selected`}</p>
    </div>
  );
}

function ArtifactCard({ output, index, isMobile }) {
  const Icon = output.icon;
  return (
    <article className={`artifact-card artifact-${output.id}`} style={{ "--artifact-index": index }}>
      <header>
        <span>{output.label}</span>
        <Icon size={17} aria-hidden="true" />
      </header>
      {output.document && (
        <a className="artifact-document-preview" href={output.href} target="_blank" rel="noreferrer" aria-label={`Open ${featuredPortfolio.name}’s full résumé PDF`}>
          <img src={featuredPortfolio.assets.resumePreview} alt={`First page of ${featuredPortfolio.name}’s résumé`} loading="lazy" decoding="async" />
        </a>
      )}
      {output.portrait && <img className="artifact-portrait" src={featuredPortfolio.assets.portrait} alt={featuredPortfolio.name} loading="lazy" decoding="async" />}
      {output.signal && (
        <Reveal
          as="div"
          className="artifact-signal"
          aria-hidden="true"
          data-motion-detail="signal"
          delay={isMobile ? 0 : motionTokens.delay.cardSignal + (index * motionTokens.stagger.standard)}
          distance={motionTokens.distance.compact}
          duration={motionTokens.duration.quick}
        >
          {output.signal.map((item) => <span key={item}>{item}</span>)}
        </Reveal>
      )}
      <div className="artifact-content">
        <p className="artifact-eyebrow">{output.eyebrow}</p>
        <h3>{output.title}</h3>
        <p>{output.body}</p>
        <ul className="artifact-highlights">
          {output.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
        </ul>
        <RuleReveal
          className="artifact-rule"
          aria-hidden="true"
          data-motion-detail="rule"
          delay={isMobile ? 0 : motionTokens.delay.cardRule + (index * motionTokens.stagger.standard)}
        />
        {output.href ? (
          <a className="artifact-action" href={output.href} target="_blank" rel="noreferrer">Open full artifact <ArrowRight size={13} aria-hidden="true" /></a>
        ) : (
          <small>{index === 1 ? "Evidence compressed into signal" : index === 2 ? "A profile that sounds like you" : "Experience aligned to ambition"}</small>
        )}
      </div>
    </article>
  );
}

function ArtifactSequence({ isMobile }) {
  const cards = outputs.map((output, index) => {
    const MotionCell = isMobile ? Reveal : Stagger.Item;
    return (
      <MotionCell
        as="div"
        className="artifact-motion-cell"
        data-artifact-motion={output.id}
        distance={isMobile ? motionTokens.distance.standard : motionTokens.distance.editorial}
        duration={isMobile ? motionTokens.duration.quick : motionTokens.duration.reveal}
        key={output.id}
      >
        <ArtifactCard output={output} index={index} isMobile={isMobile} />
      </MotionCell>
    );
  });

  if (isMobile) {
    return <div className="artifact-grid" aria-label="Your aligned career outputs" data-motion-mode="independent">{cards}</div>;
  }

  return (
    <Stagger
      as="div"
      className="artifact-grid"
      aria-label="Your aligned career outputs"
      data-motion-mode="grouped"
      delay={motionTokens.delay.outputCards}
      stagger={motionTokens.stagger.standard}
    >
      {cards}
    </Stagger>
  );
}

function IdentityHinge({ isMobile }) {
  const pillars = identityPillars.map(([label, title, body]) => {
    const MotionPillar = isMobile ? Reveal : Stagger.Item;
    return (
      <MotionPillar
        as="article"
        data-identity-pillar={label.toLowerCase()}
        distance={motionTokens.distance.standard}
        duration={isMobile ? motionTokens.duration.quick : motionTokens.duration.reveal}
        key={label}
      >
        <span>{label}</span><strong>{title}</strong><p>{body}</p>
      </MotionPillar>
    );
  });

  return (
    <div className="identity-reveal" data-motion-sequence="identity-hinge">
      <Reveal as="blockquote" distance={motionTokens.distance.standard}>
        “{featuredPortfolio.identity.quote}”
      </Reveal>
      {isMobile ? (
        <div className="identity-pillars" data-motion-mode="independent">{pillars}</div>
      ) : (
        <Stagger
          as="div"
          className="identity-pillars"
          data-motion-mode="grouped"
          delay={motionTokens.delay.identityPillars}
          stagger={motionTokens.stagger.standard}
        >
          {pillars}
        </Stagger>
      )}
    </div>
  );
}

function PricingCard({ ctaTarget, tier, isMobile }) {
  return (
    <article className={tier.featured ? "is-featured" : ""}>
      {tier.featured && (
        <RuleReveal
          className="price-featured-rule"
          aria-hidden="true"
          delay={isMobile ? 0 : motionTokens.delay.pricingRule}
          duration={motionTokens.duration.supporting}
        />
      )}
      {tier.featured && <span className="price-badge">Most popular</span>}
      <h3>{tier.name}</h3>
      <strong>{tier.price}</strong>
      <p>{tier.description}</p>
      <ul>{tier.features.map((feature) => <li key={feature}>{feature}</li>)}</ul>
      <NavLink data-analytics-action="package_selected" data-analytics-value={{"Resume Only": "resume", "Resume + Interview": "interview", "All Inclusive": "all_inclusive"}[tier.name]} to={ctaTarget} className="cinematic-price-link">Start now <ArrowRight size={17} aria-hidden="true" /></NavLink>
    </article>
  );
}

function PricingSequence({ ctaTarget, isMobile }) {
  const cards = pricingTiers.map((tier, index) => {
    const MotionCell = isMobile ? Reveal : Stagger.Item;
    return (
      <MotionCell
        as="div"
        className="pricing-motion-cell"
        data-pricing-tier={tier.name}
        data-motion-start={isMobile ? 0 : motionTokens.delay.pricingCards + (index * motionTokens.stagger.standard)}
        delay={isMobile ? 0 : motionTokens.delay.pricingCards + (index * motionTokens.stagger.standard)}
        distance={isMobile ? motionTokens.distance.standard : motionTokens.distance.subtle}
        duration={motionTokens.duration.supporting}
        key={tier.name}
      >
        <PricingCard ctaTarget={ctaTarget} tier={tier} isMobile={isMobile} />
      </MotionCell>
    );
  });

  if (isMobile) return <div className="cinematic-price-grid" data-motion-mode="independent">{cards}</div>;

  return (
    <Stagger
      as="div"
      className="cinematic-price-grid"
      data-motion-mode="grouped"
      delay={0}
      stagger={0}
    >
      {cards}
    </Stagger>
  );
}

const resolutionCards = [
  {
    id: "resume",
    content: <><span>ATS Résumé</span><h3>{featuredPortfolio.name}</h3><p className="resolution-subtitle">{featuredPortfolio.resume.subtitle}</p><div className="resolution-mini-section"><strong>Professional summary</strong><p>{featuredPortfolio.resume.summary}</p></div><div className="resolution-mini-section"><strong>Selected evidence</strong><ul>{featuredPortfolio.resume.highlights.map((item) => <li key={item}>{item}</li>)}</ul></div><small>Python · Thermodynamics · Energy Systems · Experimental Design</small></>,
  },
  {
    id: "identity",
    content: <><span>Identity Spec</span><div className="resolution-mini-section"><strong>Role</strong><p>{featuredPortfolio.identity.role}</p></div><div className="resolution-mini-section"><strong>Focus</strong><p>{featuredPortfolio.identity.focus}</p></div><div className="resolution-mini-section"><strong>Edge</strong><p>{featuredPortfolio.identity.edge}</p></div><div className="resolution-mini-section"><strong>Promise</strong><p>{featuredPortfolio.identity.promise}</p></div><small>{featuredPortfolio.identity.proof}</small></>,
  },
  {
    id: "profile",
    content: <><span>LinkedIn Profile</span><div className="resolution-profile-head"><img src={featuredPortfolio.assets.portrait} alt="" loading="lazy" decoding="async" /><div><h3>{featuredPortfolio.name}</h3><p>{featuredPortfolio.location}</p></div></div><strong className="resolution-headline">{featuredPortfolio.linkedin.headline}</strong><div className="resolution-mini-section"><strong>About</strong><p>{featuredPortfolio.linkedin.about}</p></div><div className="resolution-mini-section"><strong>Current</strong><p>{featuredPortfolio.linkedin.current}</p></div><small>Mechanical Engineering · Applied Mathematics · Energy Systems</small></>,
  },
  {
    id: "sop",
    content: <><span>Statement of Purpose</span><h3>{featuredPortfolio.sop.title}</h3><p>I am drawn to engineering problems that reveal their structure only after the underlying physics has been expressed mathematically.</p><p>My work in photovoltaic energy, underwater electromagnetics, structural dynamics, and experimental thermodynamics has taught me to connect derivation with evidence.</p><p>I want to deepen that practice through advanced study in energy systems and computational engineering.</p></>,
  },
];

function ResolutionPackage({ isMobile }) {
  const cardElements = resolutionCards.map((card, index) => {
    const MotionCard = isMobile ? Reveal : Stagger.Item;
    return (
      <MotionCard
        as="div"
        className={`resolution-card ${card.id}`}
        data-resolution-card={card.id}
        data-motion-start={isMobile ? 0 : motionTokens.delay.resolutionCards + (index * motionTokens.stagger.standard)}
        delay={isMobile ? 0 : motionTokens.delay.resolutionCards + (index * motionTokens.stagger.standard)}
        distance={isMobile ? motionTokens.distance.standard : motionTokens.distance.hero}
        duration={motionTokens.duration.supporting}
        key={card.id}
      >
        {card.content}
      </MotionCard>
    );
  });

  return (
    <div className="resolution-package" aria-hidden="true" data-motion-mode={isMobile ? "independent" : "grouped"}>
      <Reveal
        as="div"
        className="resolution-portfolio-motion"
        data-resolution-plane="portfolio"
        distance={isMobile ? motionTokens.distance.standard : motionTokens.distance.package}
        duration={motionTokens.duration.supporting}
      >
        <div className="resolution-portfolio"><img src={featuredPortfolio.assets.portfolio} alt="" loading="lazy" decoding="async" /></div>
      </Reveal>
      {isMobile ? (
        <div className="resolution-card-sequence">{cardElements}</div>
      ) : (
        <Stagger
          as="div"
          className="resolution-card-sequence"
          delay={0}
          stagger={0}
        >
          {cardElements}
        </Stagger>
      )}
    </div>
  );
}

export default function CinematicLanding({ ctaTarget }) {
  const isMobile = useLandingMobile();
  const isTouch = useMediaQuery("(hover: none), (pointer: coarse)");

  return (
    <LandingMotionProvider>
      <main className="cinematic-landing landing-page" data-testid="landing-page">
      <Reveal
        as="header"
        className="cinematic-nav"
        data-testid="landing-navigation"
        data-motion-sequence="navigation"
        distance={motionTokens.distance.compact}
        duration={motionTokens.duration.quick}
      >
        <Brand />
        <nav aria-label="Homepage sections">
          <a href="#product" data-analytics-action="section_opened" data-analytics-value="product">Product</a>
          <a href="#method" data-analytics-action="section_opened" data-analytics-value="method">Method</a>
          <a href="#pricing" data-analytics-action="section_opened" data-analytics-value="pricing">Pricing</a>
        </nav>
        <div className="cinematic-nav-actions">
          <NavLink to="/login" className="cinematic-login">Login</NavLink>
          <NavLink data-analytics-action="start_clicked" data-analytics-value="navigation" to={ctaTarget} className="cinematic-button compact" data-testid="landing-start-button">
            Start your interview <ArrowRight size={17} aria-hidden="true" />
          </NavLink>
        </div>
      </Reveal>

      <section className="cinematic-hero" data-testid="landing-hero-section">
        <div className="cinematic-hero-copy" data-testid="landing-hero-copy">
          <Reveal
            as="p"
            className="cinematic-kicker"
            distance={isMobile ? motionTokens.distance.standard : motionTokens.distance.hero}
            duration={isMobile ? motionTokens.duration.quick : motionTokens.duration.hero}
          >
            One interview. Aligned outputs.
          </Reveal>
          <HeroHeadline isMobile={isMobile} />
          <Reveal
            as="p"
            className="cinematic-lede"
            data-testid="landing-hero-description"
            delay={isMobile ? 0 : motionTokens.delay.heroLede}
            distance={isMobile ? motionTokens.distance.standard : motionTokens.distance.subtle}
            duration={motionTokens.duration.supporting}
          >
            One guided interview turns your evidence into a portfolio, résumé, profile, identity spec, and statement of purpose — all aligned to the same story.
          </Reveal>
          <Reveal
            as="div"
            className="cinematic-hero-actions"
            delay={isMobile ? 0 : motionTokens.delay.heroActions}
            distance={isMobile ? motionTokens.distance.standard : motionTokens.distance.subtle}
            duration={motionTokens.duration.supporting}
          >
            <NavLink data-analytics-action="start_clicked" data-analytics-value="hero" to={ctaTarget} className="cinematic-button" data-testid="landing-hero-primary-cta">
              Start your interview <ArrowRight size={18} aria-hidden="true" />
            </NavLink>
            <a href="#product" data-analytics-action="section_opened" data-analytics-value="product" className="cinematic-text-link">
              <span className="play-ring" aria-hidden="true"><ArrowDown size={14} /></span>
              See the complete package
            </a>
          </Reveal>
        </div>

        <HeroPackage isMobile={isMobile} />
      </section>

      <section className="output-showcase" id="product" aria-labelledby="journey-heading">
        <div className="output-showcase-layout">
          <Reveal as="div" className="journey-copy" data-motion-sequence="output-heading" distance={motionTokens.distance.standard}>
            <p className="cinematic-kicker">Evidence becomes identity</p>
            <h2 id="journey-heading">From raw evidence<br />to career clarity.</h2>
            <p>Every artifact is different in purpose. None of them tells a different story.</p>
          </Reveal>

          <ArtifactSequence isMobile={isMobile} />
        </div>

        <IdentityHinge isMobile={isMobile} />
      </section>

      <section className="evidence-section" aria-labelledby="proof-heading">
        <Reveal
          as="div"
          className="evidence-heading"
          data-motion-sequence="evidence-heading"
          distance={motionTokens.distance.standard}
        >
          <p className="cinematic-kicker">Proof, not polish</p>
          <h2 id="proof-heading">The same evidence.<br />Shaped for every room.</h2>
          <p>Aarush’s portfolio demonstrates how research, models, and prototypes become public proof without losing their technical meaning.</p>
        </Reveal>
        <EvidenceStoryMotion
          className="evidence-visual"
          data-motion-sequence="evidence-story"
          data-depth-range="16px-total"
          disableDepth={isMobile || isTouch}
        >
          {({ imageStyle, captionsStyle }) => (
            <>
              <m.div className="evidence-portfolio-surface" style={imageStyle} aria-label="Selected projects from Aarush Shah’s portfolio">
                <header><span>{featuredPortfolio.initials}</span><small>aarushs.com · Projects</small></header>
                <p className="evidence-portfolio-kicker">Published research</p>
                <h3>{featuredPortfolio.evidence[0].title}</h3>
                <p>{featuredPortfolio.evidence[0].body}</p>
                <div className="evidence-portfolio-tags"><span>Photovoltaics</span><span>Modeling</span><span>Experimental design</span></div>
              </m.div>
              <m.figcaption className="evidence-caption-layer" style={captionsStyle}>
                <EvidenceCaption className="evidence-caption" order={0}>
                  <span>Selected evidence</span>
                  <strong>{featuredPortfolio.evidence[0].title}</strong>
                  <p>Connected spectral and photovoltaic models to experimentally measured power output.</p>
                </EvidenceCaption>
                <EvidenceCaption className="evidence-caption secondary" order={1}>
                  <span>Selected evidence</span>
                  <strong>{featuredPortfolio.evidence[1].title}</strong>
                  <p>Adapted field equations for conductive seawater and simulated signal behavior in Python.</p>
                </EvidenceCaption>
              </m.figcaption>
            </>
          )}
        </EvidenceStoryMotion>
      </section>

      <section className="method-section" id="method" aria-labelledby="method-heading">
        <Reveal
          as="div"
          data-motion-sequence="method-heading"
          distance={motionTokens.distance.standard}
        >
          <p className="cinematic-kicker">The method</p>
          <h2 id="method-heading">You speak once.<br />The system listens deeply.</h2>
        </Reveal>
        <MethodProgress className="method-steps" independent={isMobile}>
          {[
            ["01", "Interview", "A guided conversation finds the decisions, contradictions, proof, and ambition hidden in your experience.", MessageCircleMore],
            ["02", "Synthesis", "The intelligence layer compresses your answers into role, focus, edge, evidence, and a coherent narrative.", Sparkles],
            ["03", "Output package", "Each deliverable is generated for its own audience while staying faithful to the same identity.", Globe2],
          ].map(([number, title, body, Icon]) => (
            <div className="method-step-content" key={number}>
              <span>{number}</span>
              <Icon size={24} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{body}</p>
            </div>
          ))}
        </MethodProgress>
      </section>

      <section className="cinematic-pricing" id="pricing" aria-labelledby="pricing-heading">
        <Reveal as="div" className="pricing-intro" data-motion-sequence="pricing-heading" distance={motionTokens.distance.subtle} duration={motionTokens.duration.supporting}>
          <p className="cinematic-kicker">Choose your package</p>
          <h2 id="pricing-heading">Start with clarity.<br />Leave with proof.</h2>
        </Reveal>
        <PricingSequence ctaTarget={ctaTarget} isMobile={isMobile} />
      </section>

      <section className="resolution-section" aria-labelledby="resolution-heading">
        <div className="resolution-copy">
          <p className="cinematic-kicker">One interview. Aligned outputs.</p>
          <h2 id="resolution-heading">Every output.<br />One identity.</h2>
          <p>A coherent career package that reflects who you are, what you do, and the impact you deliver.</p>
          <Reveal
            as="div"
            className="resolution-cta"
            data-motion-start={isMobile ? 0 : motionTokens.delay.resolutionCta}
            data-motion-trigger="own-viewport"
            delay={isMobile ? 0 : motionTokens.delay.resolutionCta}
            distance={motionTokens.distance.compact}
            duration={motionTokens.duration.supporting}
          >
            <NavLink data-analytics-action="start_clicked" data-analytics-value="footer" to={ctaTarget} className="cinematic-button">Start your interview <ArrowRight size={18} aria-hidden="true" /></NavLink>
          </Reveal>
        </div>
        <ResolutionPackage isMobile={isMobile} />
      </section>

      <footer className="cinematic-footer">
        <Brand />
        <p>One story. Ready for every opportunity.</p>
        <NavLink to="/login">Login</NavLink>
      </footer>
      </main>
    </LandingMotionProvider>
  );
}
